import { inject, Injectable } from '@angular/core';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { OidcHttpService } from '../../http/oidc-http.service';
import { UrlService } from '../url.service';
import { AuthResult, CallbackContext } from '../callback-context';
import { LoggerWrapper } from '../../../shared/providers/logger/logger-wrapper.service';
import { LoggerFactory } from '../../../shared/providers/logger/logger-factory.service';
import { OidcStorageService } from '../../storage/oidc-storage.service';
import { OpenIdConfiguration } from '../../oidc-security.service';

@Injectable({ providedIn: 'root' })
export class CodeFlowCallbackHandlerService {
  private logger: LoggerWrapper = inject(LoggerFactory).getInstance(this);

  constructor(
    private readonly oidcStorageService: OidcStorageService,
    private readonly urlService: UrlService,
    private readonly oidcHttpService: OidcHttpService
  ) {}

  // STEP 1 Code Flow
  extractCodeAndStateFromURL(urlToCheck: string): Observable<CallbackContext> {
    const code = this.urlService.getUrlParameter(urlToCheck, 'code');
    const state = this.urlService.getUrlParameter(urlToCheck, 'state');

    if (!state) {
      return throwError(() => new Error('no state in url'));
    }

    if (!code) {
      return throwError(() => new Error('no code in url'));
    }

    const initialCallbackContext = {
      code,
      refreshToken: null,
      state,
      authResult: null,
      validationResult: null,
    } as CallbackContext;

    return of(initialCallbackContext);
  }

  // STEP 2 Code Flow
  validateStateAndFetchTokensFromCode(
    callbackContext: CallbackContext,
    config: OpenIdConfiguration
  ): Observable<CallbackContext> {
    return from(this.oidcStorageService.getAuthStateControl(config.type)).pipe(
      switchMap((authStateControl) => {
        const isStateCorrect = callbackContext.state === authStateControl;

        if (!isStateCorrect) {
          return throwError(() => new Error('codeFlowCodeRequest incorrect state'));
        }

        const tokenEndpoint = config.authWellknownEndpoints.tokenEndpoint;

        return from(this.urlService.createBodyForCodeFlowCodeRequest(callbackContext.code, config)).pipe(
          switchMap((bodyForCodeFlow) => {
            return this.oidcHttpService.post(tokenEndpoint, bodyForCodeFlow).pipe(
              switchMap((response: AuthResult) => {
                callbackContext.authResult = {
                  ...response,
                  state: callbackContext.state,
                };

                return of(callbackContext);
              }),
              catchError((error) => {
                const errorMessage = `OidcService code request ${config}`;
                this.logger.error(error, '[oidc]', errorMessage);
                return throwError(() => new Error(errorMessage));
              })
            );
          })
        );
      })
    );
  }
}
